import React, { useMemo, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import StatTable from "../../../../../StatTable";
import ScrollShadow from "../../__shared/NFLTable";
import ToolErrorBoundary from "../../__shared/ToolErrorBoundary";
import { TableSkeleton } from "../../__shared/ToolStatePanel";
import { TOP_ROWS, toRedzoneRows } from "../redzoneRows";
import "./RedzoneStatsDisplay.css";

const LOGO_COLUMN = {
  Header: "",
  accessor: "logoSrc",
  disableSortBy: true,
  Cell: ({ value }) =>
    value ? <img className="nfl-rz-logo" src={value} alt="" width="22" height="22" /> : null,
};

const PLAYER_COLUMN = { Header: "Player", accessor: "player", sortDescFirst: false };

export default function RedzoneStatsDisplay({
  title,
  query,
  dataKey,
  statColumns,
  variables,
  fetchKey,
  onRetry,
  minLabel,
  emptyState,
}) {
  const skeletonColumns = ["Logo", "Player", ...statColumns.map((column) => column.Header)];

  return (
    <section className="nfl-rz-group">
      <div className="nfl-rz-group-head">
        <h2 className="nfl-rz-group-title nfl-display">{title}</h2>
        {minLabel ? <span className="nfl-rz-group-hint">{minLabel}</span> : null}
      </div>
      <ToolErrorBoundary what={title} onRetry={onRetry}>
        <React.Suspense fallback={<TableSkeleton columns={skeletonColumns} rows={6} />}>
          <RedzoneStatTable
            query={query}
            dataKey={dataKey}
            statColumns={statColumns}
            variables={variables}
            fetchKey={fetchKey}
            emptyState={emptyState}
          />
        </React.Suspense>
      </ToolErrorBoundary>
    </section>
  );
}

function RedzoneStatTable({ query, dataKey, statColumns, variables, fetchKey, emptyState }) {
  const [showAll, setShowAll] = useState(false);

  const data = useLazyLoadQuery(query, variables, {
    fetchKey,
    fetchPolicy: fetchKey > 0 ? "network-only" : "store-or-network",
  });

  if (!data[dataKey]) {
    throw new Error(`${dataKey} returned null`);
  }

  const rows = useMemo(() => toRedzoneRows(data[dataKey]), [data, dataKey]);
  const columns = useMemo(
    () => [LOGO_COLUMN, PLAYER_COLUMN, ...statColumns],
    [statColumns]
  );
  if (rows.length === 0) {
    return emptyState;
  }

  return (
    <>
      <ScrollShadow>
        <StatTable
          columns={columns}
          data={rows}
          maxRows={showAll ? undefined : TOP_ROWS}
          interactiveCells={false}
          autoResetSortBy={false}
        />
      </ScrollShadow>
      {rows.length > TOP_ROWS ? (
        <button
          type="button"
          className="nfl-link-button nfl-rz-showall"
          aria-expanded={showAll}
          onClick={() => setShowAll((open) => !open)}
        >
          {showAll ? `Show top ${TOP_ROWS}` : `Show all ${rows.length}`}
        </button>
      ) : null}
    </>
  );
}
